import React from 'react';

/**
 * ErrorBoundary -- top-level crash guard that wraps the ENTIRE app.
 *
 * If ANY child component throws during rendering, lifecycle methods, or constructors,
 * React will call getDerivedStateFromError / componentDidCatch and this component will
 * display a full-screen fallback UI instead of a blank white page.
 *
 * Because this wraps the whole app, it intentionally uses plain inline styles (no MUI)
 * to avoid depending on the same component tree that may have just crashed.
 *
 * Mounted in: src/index.js (or App.js) around <App />.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  // React calls this when a descendant throws. Returning state triggers a re-render
  // with the fallback UI.
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // Logs the error + component stack for debugging. Runs after the fallback is committed.
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    /**
     * Reset activeSection to 'projects' -- crash-loop prevention.
     *
     * The app reads `eis_activeSection` from localStorage on startup to restore the last
     * visited section (e.g. "cad-agent", "documents"). If the crash was caused by that
     * specific section's component, reloading would immediately crash again in an infinite
     * loop. By forcing the section back to the safe "projects" page, the user can reload
     * without re-entering the broken section.
     */
    localStorage.setItem('eis_activeSection', 'projects');

    /**
     * Clear ALL Cache Storage entries (Service Worker caches, etc.).
     * This handles cases where stale cached assets (old JS bundles after a deploy)
     * are the root cause of the crash. Deleting the caches forces the browser to
     * fetch fresh assets on the next load.
     */
    if ('caches' in window) {
      caches.keys().then(names => names.forEach(name => caches.delete(name)));
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Full-screen fallback -- uses only inline styles so it cannot itself crash
      // due to a broken theme provider or MUI context.
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: '2rem',
          textAlign: 'center',
        }}>
          <h2 style={{ marginBottom: '0.5rem', color: '#333' }}>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem', maxWidth: 400 }}>
            The application encountered an error. This can happen after an update — try refreshing.
          </p>
          <button
            onClick={this.handleRetry}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Refresh Page
          </button>
          <p style={{ color: '#999', fontSize: '0.75rem', marginTop: '2rem' }}>
            If the problem persists, please contact support.
          </p>
        </div>
      );
    }

    // No error -- render children normally.
    return this.props.children;
  }
}

export default ErrorBoundary;
